import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase'; // Feltételezve, hogy már van firebase konfigurációnk
import ContactInfo from '../components/ContactInfo';
import { Link } from 'react-router-dom';

interface FormData {
  name: string;
  email: string;
  company: string;
  phone: string;
  message: string;
  createdAt: Date;
}

const Home = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    createdAt: new Date(),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await addDoc(collection(db, 'applications'), {
        ...formData,
        createdAt: new Date()
      });
      
      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', phone: '', message: '', createdAt: new Date() });
      
      // Opcionális: küldjünk értesítő emailt magunknak
      // Ezt később implementálhatjuk Firebase Cloud Functions segítségével
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      // 3 másodperc múlva töröljük az állapotüzenetet
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <main className="flex-grow container mx-auto px-4 py-8">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-800 rounded-xl shadow-lg mb-12 p-8 text-white">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Minden vállalkozásnak legyen saját honlapja program
          </h1>
          <p className="text-xl mb-6">
            9 milliárd forint keretösszegű támogatás KKV-k részére
          </p>
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 mb-8">
            <p className="text-lg">
              Pályázzon most akár 1.100.000 Ft vissza nem térítendő támogatásra!
            </p>
          </div>
        </div>

        {/* CTA gombok */}
        <div className="flex flex-col items-center gap-6">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href="https://forms.gle/mfDWN7v71sycLqbz8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-white text-purple-900 rounded-lg font-semibold hover:bg-purple-100 transition-colors duration-200"
            >
              Jelentkezési űrlap megnyitása
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
            
            <Link 
              to="/tudnivalok"
              className="inline-flex items-center px-8 py-4 bg-purple-500 hover:bg-purple-400 text-white rounded-lg font-semibold transition-colors duration-200 border border-white/20"
            >
              Tudnivalók megtekintése
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>

            <a 
              href="#visszahivas"
              className="inline-flex items-center px-8 py-4 bg-purple-500 hover:bg-purple-400 text-white rounded-lg font-semibold transition-colors duration-200 border border-white/20"
            >
              Kérjen visszahívást
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </a>
          </div>
          
          <p className="text-white/80 text-sm bg-white/10 px-4 py-2 rounded-full">
            ℹ️ A jelentkezési űrlap kitöltése csak előzetes egyeztetés után lehetséges
          </p>
        </div>
      </div>

      {/* Csomagok */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="bg-gradient-to-b from-purple-50 to-purple-100 rounded-xl shadow-lg p-6 border border-purple-200">
          <div className="text-purple-700 font-bold text-xl mb-4">
            "A" Digitalizációs alapcsomag
          </div>
          <ul className="space-y-2 mb-6 text-purple-900">
            <li>• Weboldal fejlesztés</li>
            <li>• Domain regisztráció</li>
            <li>• Tárhely szolgáltatás</li>
            <li>• SSL tanúsítvány</li>
          </ul>
          <div className="text-2xl font-bold text-purple-700">
            400.000 Ft
          </div>
        </div>

        <div className="bg-gradient-to-b from-indigo-50 to-indigo-100 rounded-xl shadow-lg p-6 border border-indigo-200">
          <div className="text-indigo-700 font-bold text-xl mb-4">
            "B" Közösségi média alapcsomag
          </div>
          <ul className="space-y-2 mb-6 text-indigo-900">
            <li>• Közösségi média jelenlét kialakítása</li>
            <li>• Tartalomstratégia kidolgozása</li>
            <li>• Hirdetési kampányok beállítása</li>
            <li>• Analitika beállítása</li>
          </ul>
          <div className="text-2xl font-bold text-indigo-700">
            400.000 Ft
          </div>
        </div>

        <div className="bg-gradient-to-b from-violet-50 to-violet-100 rounded-xl shadow-lg p-6 border border-violet-200">
          <div className="text-violet-700 font-bold text-xl mb-4">
            Bővített csomag (A+B)
          </div>
          <ul className="space-y-2 mb-6 text-violet-900">
            <li>• Mindkét alapcsomag tartalma</li>
            <li>• Webshop fejlesztés</li>
            <li>• Marketing automatizáció</li>
            <li>• Bővített támogatás</li>
          </ul>
          <div className="text-2xl font-bold text-violet-700">
            1.100.000 Ft
          </div>
        </div>
      </div>

      {/* Pályázati feltételek */}
      <div className="bg-gradient-to-br from-purple-100 to-indigo-100 rounded-xl shadow-lg p-8 mb-12 border border-purple-200">
        <h2 className="text-2xl font-bold mb-6 text-purple-900">Pályázati feltételek vállalkozásoknak</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div>
              <h3 className="font-bold mb-3 text-purple-800">Kik nyújthatnak be pályázatot?</h3>
              <ul className="space-y-2 text-purple-900">
                <li>• Magyarországi székhellyel rendelkező mikro- és kisvállalkozás</li>
                <li>• Köztartozásmentes adózói adatbázisban (KOMA) szereplő</li>
                <li>• Legalább 1 fő létszámmal rendelkező</li>
                <li>• Csőd, felszámolás vagy végelszámolás alatt nem álló</li>
                <li>• Nem rendelkezik saját, működő honlappal, vagy az inaktív/fejlesztésre szorul</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-3 text-purple-800">Támogatható tevékenységek</h3>
              <ul className="space-y-2 text-purple-900">
                <li>• Digitális eszközök az internetes megjelenéshez</li>
                <li>• Közösségi média megjelenés</li>
                <li>• Weboldal optimalizálás</li>
                <li>• Kiber-ellenállóképesség növelése</li>
                <li>• Tartalomfejlesztés támogatása</li>
                <li>• Internetes értékesítés támogatása (pl. webshop)</li>
              </ul>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="font-bold mb-3 text-purple-800">Támogatás részletei</h3>
              <ul className="space-y-2 text-purple-900">
                <li>• Igényelhető támogatás: 400.000 Ft - 1.500.000 Ft</li>
                <li>• Támogatási intenzitás: 100% (önerő nem szükséges)</li>
                <li>• Bruttó támogatás (ÁFA-t tartalmazza)</li>
                <li>• Projekt időtartam: 24 hónap az utalvány kiállításától</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-3 text-purple-800">Szükséges dokumentumok</h3>
              <ul className="space-y-2 text-purple-900">
                <li>• Aláírási címpéldány vagy aláírás-minta</li>
                <li>• Köztartozásmentes adózói minőségről szóló igazolás (ha szükséges)</li>
                <li className="text-sm italic">Dokumentumok benyújtása február 3-tól</li>
              </ul>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 mt-4">
              <p className="text-purple-900 font-medium">
                ⚠️ Fontos: A támogatási kérelem benyújtására konzorciumi formában nincs lehetőség.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Új szekció a Google Forms linkkel */}
      <div className="bg-gradient-to-r from-purple-800 to-indigo-900 rounded-xl shadow-lg p-8 text-white text-center mb-12">
        <h2 className="text-2xl font-bold mb-4">Már egyeztetett a Webforyou-val?</h2>
        <p className="mb-8 text-lg">
          Ha már egyeztetett velünk és szeretne jelentkezni a pályázatra, töltse ki az űrlapunkat!
        </p>
        <a 
          href="https://forms.gle/mfDWN7v71sycLqbz8"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block px-8 py-4 bg-white text-purple-900 rounded-lg font-semibold hover:bg-purple-100 transition-colors duration-200"
        >
          Jelentkezési űrlap megnyitása
        </a>
      </div>

      {/* Új Kapcsolat Section */}
      <div id="visszahivas" className="bg-gradient-to-r from-purple-800 to-indigo-900 rounded-xl shadow-lg p-8 text-white text-center mb-12">
        <h2 className="text-2xl font-bold mb-4">Visszahívást kérek</h2>
        <p className="mb-6 text-purple-100">
          Adja meg elérhetőségeit és szakértő kollégánk hamarosan felveszi Önnel a kapcsolatot
        </p>
        
        <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-8">
          <a 
            href="mailto:nagyroland@webforyou.hu"
            className="flex items-center space-x-2 text-white hover:text-purple-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-lg">nagyroland@webforyou.hu</span>
          </a>
        </div>

        {/* Jelentkezési űrlap */}
        <div className="max-w-2xl mx-auto bg-white/10 backdrop-blur-sm rounded-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Visszahívási igény beküldése</h3>
          
          {submitStatus === 'success' && (
            <div className="mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg text-green-100">
              Jelentkezését sikeresen rögzítettük! Hamarosan felvesszük Önnel a kapcsolatot.
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-100">
              Hiba történt a jelentkezés során. Kérjük, próbálja újra később!
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Kapcsolattartó neve"
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="E-mail cím"
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Cég neve"
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Telefonszám"
                required
                className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
            </div>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Üzenet (opcionális)"
              rows={4}
              className="w-full px-4 py-2 rounded-lg bg-white/20 border border-purple-300/30 placeholder-purple-200 text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full md:w-auto px-8 py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-lg transition-colors duration-200 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Küldés...' : 'Visszahívást kérek'}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
};

export default Home; 